'use client';

import { useCallback, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

type WindowFeatures = {
  popup: boolean;
  width: number;
  height: number;
  top?: number;
  left?: number;
};

const WindowFeaturesToString = (features: WindowFeatures): string => {
  const { popup, width, height } = features;

  const base = `popup=${popup ? 'yes' : 'no'},width=${width},height=${height}`;
  if (typeof window === 'undefined') {
    return base;
  }

  if (!features.left) {
    const screenLeft = window.screenLeft !== undefined ? window.screenLeft : 0;

    const screenWidth = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : window.screen.width;

    features.left = screenWidth / 2 - width / 2 + screenLeft;
  }

  if (!features.top) {
    const screenTop = window.screenTop !== undefined ? window.screenTop : 0;

    const screenHeight = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : window.screen.height;

    features.top = features.top ?? screenHeight / 2 - height / 2 + screenTop;
  }

  return `${base},left=${features?.left ?? 0},top=${features?.top ?? 0}`;
};

type WindowOption = {
  ref: Window;
  name: string;
  url: string;
  features: WindowFeatures;
  onLoad?: () => void;
  onBlocked?: () => void;
  onUnload?: () => void;
};

type OpenParams = Omit<WindowOption, 'ref'>;

export const useWindowOpener = () => {
  const [map, setMap] = useState<Map<string, WindowOption>>(new Map());

  const needChecking = useMemo(() => map.size !== 0, [map]);

  useInterval(
    () => {
      for (const { name, ref } of map.values()) {
        if (!ref || ref.closed) {
          close(name);
        }
      }
    },
    needChecking ? 500 : null
  );

  const isOpen = useCallback(
    (name: string) => {
      return map.has(name);
    },
    [map]
  );

  const open = useCallback(
    (params: OpenParams) => {
      setTimeout(() => {
        const {
          name,
          url,
          features,
          onLoad = undefined,
          onBlocked = undefined,
        } = params;

        const exist = isOpen(name);
        if (exist) {
          return;
        }

        const goTo = url.startsWith('http')
          ? url
          : new URL(url, window.location.href).toString();

        const newWindow = window.open(
          goTo,
          name,
          WindowFeaturesToString(features)
        );

        if (!newWindow) {
          if (typeof onBlocked === 'function') {
            onBlocked();
          }
          return;
        }

        if (typeof onLoad === 'function') {
          onLoad();
        }

        setMap(
          (prev) =>
            new Map(
              prev.set(name, {
                ...params,
                ref: newWindow,
              })
            )
        );
      }, 0);
    },
    [map, isOpen]
  );

  const close = useCallback(
    (name: string) => {
      const exist = isOpen(name);
      if (!exist) {
        return;
      }

      const item = map.get(name);
      if (!item?.ref) {
        return;
      }

      if (typeof item.onUnload === 'function') {
        item.onUnload();
      }

      const { ref } = item;
      if (!ref.closed) {
        ref.close();
      }

      setMap((prev) => {
        prev.delete(name);
        return new Map(prev);
      });
    },
    [map]
  );

  return {
    isOpen,
    open,
    close,
  };
};
